import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";
import {
  OrganizationEditor,
  type EditableOrganization,
} from "../../components/organization/OrganizationEditor";

export function OrganizationCreatePage() {
  const navigate = useNavigate();
  const [value, setValue] = useState<EditableOrganization>({
    id: "new",
    name: "",
    slug: "",
    locale: "en-US",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api("/organizations", {
        method: "POST",
        body: JSON.stringify({
          name: value.name,
          slug: value.slug,
          locale: value.locale,
          timezone: value.timezone,
        }),
      });
      navigate("/settings/organization/edit/overview", { replace: true });
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t("Organization could not be created"),
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <SettingsPage
      title={t("Create organization")}
      description={t(
        "Set up a new customer organization. You can invite people and configure resources after creation.",
      )}
    >
      {error ? (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      <div className="mt-5">
        <OrganizationEditor
          value={value}
          saving={saving}
          title={t("Create organization")}
          saveLabel="Create organization"
          onChange={(next) => {
            const generatedSlug = value.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "");
            const slugWasGenerated =
              !value.slug || value.slug === generatedSlug;
            const nextSlug =
              next.name !== value.name && slugWasGenerated
                ? next.name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                : next.slug;
            setValue({ ...next, slug: nextSlug });
          }}
          onSave={() => void save()}
          onCancel={() => navigate("/settings/organization")}
        />
      </div>
    </SettingsPage>
  );
}
